import { useScrimDismiss } from './useScrimDismiss';

/**
 * VOILE partagé de toutes les modales maison — le pendant visuel/gestuel de
 * `useModalA11y` (qui, lui, prend en charge focus, `inert`, Échap et défilement).
 *
 * Il existe parce que la MÊME dizaine de lignes était recopiée dans dix-neuf fichiers :
 * le voile plein écran, son opacité, son centrage, et surtout sa règle de fermeture. Une
 * règle recopiée dix-neuf fois est une règle qu'on corrige une fois sur dix-neuf — la
 * garde du geste vit donc ICI, une seule fois (cf. `useScrimDismiss` pour le mécanisme :
 * un `click` naît sur l'ANCÊTRE COMMUN du `pointerdown` et du `pointerup`, si bien qu'un
 * geste commencé dans un champ et relâché sur le voile visait le voile).
 *
 * Le panneau reste à la charge de l'appelant : c'est lui qui porte `role="dialog"`,
 * `aria-modal` et la `ref` passée à `useModalA11y`. Ce composant n'habille que le fond.
 */
interface ModalOverlayProps {
  /**
   * Fermeture demandée par un clic FRANC sur le voile (appui ET relâchement dessus).
   * `undefined` la désactive — c'est la forme qu'on donne au verrouillage pendant un
   * appel réseau : `onDismiss={busy ? undefined : onClose}`.
   */
  onDismiss?: ((e: React.MouseEvent<Element>) => void) | undefined;
  children: React.ReactNode;
  /** Remplace les classes du voile pour les rares fonds non centrés. */
  className?: string;
}

const DEFAULT_SCRIM_CLASS = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';

export function ModalOverlay({
  onDismiss,
  children,
  className,
}: ModalOverlayProps): React.JSX.Element {
  const scrim = useScrimDismiss(onDismiss);

  return (
    /* Le voile n'est PAS un contrôle : fermer au clic dehors est un RACCOURCI de confort,
       doublé au clavier par `Échap` (cf. `useModalA11y`) et par le bouton « Fermer » de
       chaque modale. Lui donner un `role`/`tabIndex` ajouterait une escale de tabulation
       redondante et un élément interactif fantôme dans l'arbre d'accessibilité.
       (`jsx-a11y` ne voit plus les gestionnaires, passés en `{...scrim}` : plus de
       `eslint-disable` à porter, mais la justification reste la même.) */
    <div className={className ?? DEFAULT_SCRIM_CLASS} {...scrim}>
      {children}
    </div>
  );
}
